import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import { onUpdateUser } from "../../features/profile/presentation/store/OnboardingThunk";
import { UpdateUserParams } from "../../features/profile/domain/usecases/updateUser";
import UserInfo from "../../features/profile/domain/entities/user";
import {
	getCurrentRealCoordinates,
	getProfileLocationSource,
	setCurrentRealCoordinates,
} from "../utilities/locationStorage";

const COORDINATE_EPSILON = 0.00001;

export const useLocationShared = () => {
	const dispatch: AppDispatch = useDispatch();
	const user = useSelector((state: RootState) => state.onboarding.user);
	const userAgent = navigator.userAgent;
	const [permissionState, setPermissionState] = useState<string | null>(null);
	const isIphoneDevice = /iPhone|iPad|iPod/i.test(userAgent);

	const handleLocationUpdate = useCallback((latitude: number, longitude: number): void => {
		setCurrentRealCoordinates(latitude, longitude);
		const updateParams = new UpdateUserParams({
			lat: latitude.toString(),
			lng: longitude.toString(),
			profile_location_source: "browser_location",
		} as UserInfo);
		dispatch(onUpdateUser(updateParams));
	}, [dispatch]);

	const getLocation = useCallback((
		successCallback: (latitude: number, longitude: number) => void,
		errorCallback?: () => void
	): void => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					successCallback(latitude, longitude);
				},
				(error) => {
					console.error("Location denied:", error);
					localStorage.setItem("LOCATION_PERMISSION_STATE", "denied");
					errorCallback?.();
				}
			);
		} else {
			console.error("Geolocation not supported");
			localStorage.setItem("LOCATION_PERMISSION_STATE", "denied");
			errorCallback?.();
		}
	}, []);

	const checkGeolocationPermission = useCallback(async () => {
		try {
			if (!navigator.permissions || !navigator.permissions.query) {
				// Browsers without a permission-query API must wait for an explicit
				// location-button tap. Opening the app must not request permission.
				setPermissionState("prompt");
				return;
			}
			const permission = await navigator.permissions.query({ name: "geolocation" });
			setPermissionState(permission.state);
			localStorage.setItem("LOCATION_PERMISSION_STATE", permission.state);
			permission.onchange = () => {
				localStorage.setItem("LOCATION_PERMISSION_STATE", permission.state);
				setPermissionState(permission.state);
			};
		} catch (error) {
			console.error("Error checking geolocation permission", error);
		}
	}, []);

	useEffect(() => {
		if (permissionState !== "granted" || user?.status_onboarding !== 1 ||
			getProfileLocationSource() === "manual_selection") return;
		const { lat: storedLat, lng: storedLng } = getCurrentRealCoordinates();
		const currentLat = storedLat ? Number(storedLat) : null;
		const currentLng = storedLng ? Number(storedLng) : null;
		const hasStoredCoordinates =
			currentLat !== null &&
			currentLng !== null &&
			Number.isFinite(currentLat) &&
			Number.isFinite(currentLng);
		let cancelled = false;
		getLocation((latitude: number, longitude: number) => {
			if (cancelled || getProfileLocationSource() === "manual_selection") return;
			const hasChanged =
				!hasStoredCoordinates ||
				Math.abs(latitude - (currentLat as number)) > COORDINATE_EPSILON ||
				Math.abs(longitude - (currentLng as number)) > COORDINATE_EPSILON;
			if (hasChanged) {
				handleLocationUpdate(latitude, longitude);
				return;
			}
			setCurrentRealCoordinates(latitude, longitude);
		});
		return () => { cancelled = true; };
	}, [getLocation, handleLocationUpdate, permissionState, user?.status_onboarding, user?.profile_location_source]);
	const iosLocation = () => {
		setPermissionState("granted");
		localStorage.setItem("LOCATION_PERMISSION_STATE", "granted");
	};

	useEffect(() => {
		checkGeolocationPermission();
	}, [checkGeolocationPermission]);
	return { permissionState, iosLocation, isIphoneDevice };
};
